import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { emailSchema } from "~/schemas/SignUp/Email";
import { useDraftSync } from "~/shared/hooks/useDraftSync";
import { useFunnelTracking } from "~/shared/hooks/useFunnelTracking";
import { SignUpFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { Input } from "~/ui";
import { tw } from "~/utils";
import { ActionButtons } from "../ActionButtons";
import { ErrorMessage } from "../ErrorMessage";
import { FormContentContainer } from "../FormContentContainer";

export const EmailForm = ({
  onPrev,
  onNext,
  stepId = "email",
}: SignUpFormProps & { stepId?: string }) => {
  const { patient, setPatientField } = useSignUpStore();
  const { email } = patient;
  const { flushDraftSync } = useDraftSync();
  const { trackComplete, trackBack, trackErrors } = useFunnelTracking({
    step: "email",
  });
  const [isChecking, setIsChecking] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<z.infer<typeof emailSchema>>({
    mode: "onBlur",
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: email ?? "",
    },
  });

  const emailValue = watch("email");
  const isValidEmail = emailValue && !errors.email;

  const onSubmit: SubmitHandler<z.infer<typeof emailSchema>> = async (data) => {
    setPatientField("email", data.email);
    setIsChecking(true);
    try {
      const result = await flushDraftSync({
        ...patient,
        email: data.email,
      });
      if (result?.emailExists) {
        toast.error(
          "This email is already registered. Please use a different email.",
        );
        return;
      }
      await trackComplete();
      onNext();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsChecking(false);
    }
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientField("email", "");
    trackBack();
    onPrev();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="flex w-full max-w-[570px] flex-col rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-16 self-center sm:size-20" />
        <div>
          <h1 className="font-display text-xl sm:text-2xl">
            What&apos;s your email address?
          </h1>
        </div>
        <div className={tw("flex w-full flex-col gap-5", !errors && "mb-8")}>
          <Input
            placeholder="Email*"
            id="email"
            compact
            {...register("email")}
          />
        </div>
        {errors.email && <ErrorMessage message={errors.email.message ?? ""} />}
      </FormContentContainer>
      <ActionButtons
        onPrev={handlePrev}
        isNextLoading={isChecking}
        isNextDisabled={!isValidEmail}
        helperText="Enter a valid email to continue"
      />
    </form>
  );
};
